import { LocaleString } from '@/api-client/models/utilization/Contracts'

type FormatType =
  | 'intl-number'
  | 'intl-datetime'
  | 'number-floor'
  | 'regex'
  | 'format-string'
  | 'locale-format-string'
  | 'format-function'
  | 'mapping'
  | 'locale-mapping'
  | 'epoch-day'
  | 'multiply'
  | 'isodow'
  | 'hour'
  | 'hour-range'

type FormatStep<Type extends FormatType = FormatType> = Type extends 'intl-number'
  ? {
      type: 'intl-number'
    } & Intl.NumberFormatOptions
  : Type extends 'intl-datetime'
  ? {
      type: 'intl-datetime'
    } & Intl.DateTimeFormatOptions & { week?: 'numeric'; quarter?: 'short' }
  : Type extends 'number-floor'
  ? {
      type: 'number-floor'
    }
  : Type extends 'regex'
  ? {
      type: 'regex'
      search: string
      replace: string
      flags?: string
    }
  : Type extends 'format-string'
  ? {
      type: 'format-string'
      format: string
    }
  : Type extends 'locale-format-string'
  ? {
      type: 'locale-format-string'
      format: LocaleString
    }
  : Type extends 'format-function'
  ? {
      type: 'format-function'
      format: (value: number | string, locale: string) => string
    }
  : Type extends 'locale-mapping'
  ? {
      type: 'locale-mapping'
      mapping: { [key: string]: LocaleString }
    }
  : Type extends 'mapping'
  ? {
      type: 'mapping'
      mapping: { [key: string]: string }
    }
  : Type extends 'epoch-day'
  ? {
      type: 'epoch-day'
    }
  : Type extends 'multiply'
  ? {
      type: 'multiply'
      value: number
    }
  : Type extends 'isodow'
  ? {
      type: 'isodow'
      weekday: Intl.DateTimeFormatOptions['weekday']
    }
  : Type extends 'hour'
  ? {
      type: 'hour'
      hour: Intl.DateTimeFormatOptions['hour']
      minute?: Intl.DateTimeFormatOptions['minute']
    }
  : Type extends 'hour-range'
  ? {
      type: 'hour-range'
      hour: Intl.DateTimeFormatOptions['hour']
      minute?: Intl.DateTimeFormatOptions['minute']
    }
  : never

export type FormatOptions = {
  join?: string
  joinMethod?: 'sequential' | 'parallel'
  format?: FormatStep
  steps?: FormatOptions[]
}

function getWeekNumber(date: Date) {
  const onejan = new Date(date.getFullYear(), 0, 1)
  return Math.ceil(((date.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7)
}

function fmt(step: FormatStep, value: number | string, locale: string) {
  switch (step.type) {
    case 'intl-number':
      return new Intl.NumberFormat(locale, step).format(
        typeof value === 'string' ? parseFloat(value) : value
      )
    case 'intl-datetime':
      const date = new Date(value)
      let string = new Intl.DateTimeFormat(locale, step).format(date)
      if (step.week) {
        const weekNumber = getWeekNumber(date)
        string = weekNumber.toString()
      }
      if (step.quarter) {
        const quarter = Math.floor(date.getMonth() / 3) + 1
        string = 'Q' + quarter.toString() + ' ' + string
      }
      return string
    case 'number-floor':
      return Math.floor(parseFloat(value.toString())).toString()
    case 'regex':
      return value.toString().replace(new RegExp(step.search, step.flags), step.replace)
    case 'format-string':
      return step.format.replace('%s', value.toString())
    case 'locale-format-string':
      return step.format[locale].replace('%s', value.toString())
    case 'format-function':
      return step.format(value, locale)
    case 'mapping':
      return step.mapping[value.toString()] ?? value.toString()
    case 'locale-mapping':
      return step.mapping[value.toString()]?.[locale] ?? value.toString()
    case 'epoch-day':
      return new Date(
        (typeof value === 'number' ? value : parseFloat(value)) * 24 * 60 * 60 * 1000
      ).toLocaleDateString(locale)
    case 'multiply':
      return parseFloat(value.toString()) * step.value
    case 'isodow':
      const dow = typeof value === 'number' ? value : parseFloat(value)
      const d = new Date()
      d.setDate(d.getDate() + ((dow - d.getDay() + 7) % 7))
      return d.toLocaleDateString(locale, {
        weekday: step.weekday
      })
    case 'hour':
      const hour = typeof value === 'number' ? value : parseFloat(value)
      const h = new Date()
      h.setHours(hour)
      h.setMinutes(0)
      return h.toLocaleTimeString(locale, {
        hour: step.hour,
        minute: step.minute
      })
    case 'hour-range':
      const hourRange = typeof value === 'number' ? value : parseFloat(value)
      const hRangeFrom = new Date()
      hRangeFrom.setHours(hourRange)
      hRangeFrom.setMinutes(0)
      const hRangeTo = new Date()
      hRangeTo.setHours(hourRange + 1)
      hRangeTo.setMinutes(0)
      return `${hRangeFrom.toLocaleTimeString(locale, {
        hour: step.hour,
        minute: step.minute
      })} - ${hRangeTo.toLocaleTimeString(locale, {
        hour: step.hour,
        minute: step.minute
      })}`
  }
  // @ts-ignore
  throw new Error(`Unknown format type: ${step.type}`)
}

export function formatter(
  value: number | string | undefined,
  locale: string,
  options?: FormatOptions | undefined,
  undefinedValue?: string
): string {
  if (value === undefined) return undefinedValue ?? '-'
  const steps = options?.steps ?? []
  const formatted = options?.format ? fmt(options.format, value, locale) : value
  if (steps.length === 0) return formatted.toString()
  if (options?.joinMethod === 'parallel')
    return steps.map((child) => formatter(formatted, locale, child)).join(options.join)
  return steps.reduce((acc, child) => formatter(acc, locale, child), formatted).toString()
}
